import { useUserStore } from '@talon/web/src/context';
import { Avatar, Box, ButtonExtendedProps, Menu, Text, TextProps } from 'grommet';
import { Home, Logout, Organization, User, UserAdmin, UserExpert } from 'grommet-icons';
import { capitalize } from 'lodash';
import { observer } from 'mobx-react-lite';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchApiRelease, formatBuildLabel, WEB_RELEASE } from '../../utils/release';

export type UserMenuProps = {
  textColor?: TextProps['color'];
};

export const UserMenu = observer(({ textColor = 'accent-1' }: UserMenuProps) => {
  const userStore = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const [apiRelease, setApiRelease] = useState<string | null>(null);

  useEffect(() => {
    fetchApiRelease().then(setApiRelease);
  }, []);

  const doLogout = () => {
    userStore.logout();
    posthog.reset();
    navigate('/login');
  };

  const profile: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text>Profile</Text>
      </Box>
    ),
    onClick: () => navigate('/profile'),
    icon: (
      <Box pad="small">
        <User />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/profile',
  };
  const account: ButtonExtendedProps = {
    label: <Box alignSelf="center">Account</Box>,
    onClick: () => navigate('/account'),
    icon: (
      <Box pad="small">
        <Organization />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/account',
  };
  const logout: ButtonExtendedProps = {
    label: <Box alignSelf="center">Logout</Box>,
    onClick: () => doLogout(),
    icon: (
      <Box pad="small">
        <Logout />
      </Box>
    ),
    color: 'accent-1',
    justify: 'start',
  };

  const adminMenu: ButtonExtendedProps = {
    label: <Box alignSelf="center">Admin</Box>,
    onClick: () => navigate('/admin'),
    icon: (
      <Box pad="small">
        <UserAdmin />
      </Box>
    ),
    color: 'accent-1',
    justify: 'start',
    active: location.pathname === '/admin',
  };

  // The admin area has its own header without the regular app's navigation; this is the way back
  const appMenu: ButtonExtendedProps = {
    label: <Box alignSelf="center">Talon app</Box>,
    onClick: () => navigate('/'),
    icon: (
      <Box pad="small">
        <Home />
      </Box>
    ),
    justify: 'start',
  };

  // const payrollMenu: ButtonExtendedProps = {
  //   label: <Box alignSelf="center">Payroll</Box>,
  //   onClick: () => navigate('/payroll'),
  //   icon: (
  //     <Box pad="small">
  //       <Money />
  //     </Box>
  //   ),
  //   color: 'accent-1',
  //   justify: 'start',
  //   active: location.pathname === '/payroll',
  // };

  const tmProfile: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text>My TM Profile</Text>
      </Box>
    ),
    onClick: () => navigate('/talent-manager'),
    icon: (
      <Box pad="small">
        <UserExpert />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/talent-manager',
  };

  const supportInboxMenu: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text>Support Inbox</Text>
      </Box>
    ),
    onClick: () => navigate('/support-inbox'),
    icon: (
      <Box pad="small">
        <UserExpert />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/support-inbox',
  };

  // Which web + API builds this user is running, so a bug report can say exactly what was live
  const buildInfo: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text size="xsmall" color="light-6">
          {formatBuildLabel(WEB_RELEASE, apiRelease)}
        </Text>
      </Box>
    ),
    onClick: () => {
      navigator.clipboard
        ?.writeText(`web ${WEB_RELEASE} api ${apiRelease ?? 'unknown'}`)
        .then(() => toast.info('Build info copied'));
    },
    justify: 'start',
  };

  const menuItems: ButtonExtendedProps[] = userStore.user?.is_admin
    ? [adminMenu, appMenu, profile, account, logout] // admin
    : userStore.user.performer_id
      ? [profile, logout] // performer user
      : userStore.user.is_talent_manager
        ? [tmProfile, logout] // talent manager
        : [profile, account, logout]; // agency user

  // Support Inbox is its own route (not a tab under /admin) so both admins and non-admin support
  // agents need a direct link to it, regardless of which role bucket they otherwise fall into above.
  if (userStore.user?.is_admin || userStore.user?.is_support_agent) {
    menuItems.unshift(supportInboxMenu);
  }
  menuItems.push(buildInfo);
  return (
    <Box>
      <Menu
        justifyContent="center"
        dropBackground="dark-1"
        icon={
          <Box direction="row" align="center" justify="center" wrap={true} gap="xsmall" pad={{ vertical: '3px' }}>
            <Avatar background="accent-2" size="42px">
              {userStore.user?.first_name?.substring(0, 1)}
              {userStore.user?.last_name?.substring(0, 1)}
            </Avatar>
            <Box>
              <Text color={textColor}>{`${userStore.user?.first_name} ${userStore.user?.last_name}`}</Text>
              <Text size="small" weight={300} color={textColor}>
                {userStore.user?.active_group ? capitalize(userStore.user?.active_group?.name) : ''}
              </Text>
            </Box>
          </Box>
        }
        items={menuItems}
      ></Menu>
    </Box>
  );
});
